import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  Policy,
  PolicyChangeProposal,
  Account,
} from '@mls-wallet/shared-types';
import type { PolicyWithChange } from '../utils/policyProposalUtils';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import { PoliciesTable } from './policies-table';
import { PolicyDetailSheet } from './policy-detail-sheet';
import { NewPolicyDialog } from './new-policy-dialog';
import { EditPolicyDialog } from './edit-policy-dialog';
import { PolicyChangeComparisonDialog } from './policy-change-comparison-dialog';
import { useCreatePolicyProposal } from '@/hooks/admin/policy/use-create-policy-proposal';
import { usePreviewPolicyProposalImpactDraft } from '@/hooks/admin/policy/use-preview-policy-proposal-impact-draft';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import {
  computeFinalPolicyState,
  getPolicyState,
} from '../utils/policyProposalUtils';
import { PolicyImpactPreviewDialog } from './policy-impact-preview-dialog';
import type { PolicyProposalImpactPreview } from '@mls-wallet/shared-types';

interface EditPoliciesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policies: Policy[];
  accounts: Account[];
  onProposalCreated: () => void;
}

export function EditPoliciesDialog({
  open,
  onOpenChange,
  policies,
  accounts,
  onProposalCreated,
}: EditPoliciesDialogProps) {
  const { organization } = useMyOrganization();
  const { createProposal } = useCreatePolicyProposal();
  const { previewImpact } = usePreviewPolicyProposalImpactDraft();

  const [selectedPolicy, setSelectedPolicy] = useState<PolicyWithChange | null>(
    null
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isNewPolicyDialogOpen, setIsNewPolicyDialogOpen] = useState(false);
  const [isEditPolicyDialogOpen, setIsEditPolicyDialogOpen] = useState(false);
  const [isComparisonDialogOpen, setIsComparisonDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isImpactPreviewOpen, setIsImpactPreviewOpen] = useState(false);
  const [impactPreview, setImpactPreview] =
    useState<PolicyProposalImpactPreview | null>(null);

  // Store policy changes instead of modified policies
  const [policyChanges, setPolicyChanges] = useState<PolicyChangeProposal[]>(
    []
  );
  const [originalPolicies, setOriginalPolicies] = useState<Policy[]>([]);

  // Compute the displayed policies by applying changes
  const displayedPolicies = useMemo(() => {
    return computeFinalPolicyState(originalPolicies, policyChanges, accounts);
  }, [originalPolicies, policyChanges, accounts]);

  // Initialize when dialog opens (only once when 'open' becomes true)
  useEffect(() => {
    if (open) {
      setOriginalPolicies([...policies]);
      setPolicyChanges([]);
    }
    // Note: intentionally NOT depending on `policies` to avoid re-initialization
    // while user is editing. We capture the policies snapshot when dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedPolicy(null);
      setIsSheetOpen(false);
      setIsNewPolicyDialogOpen(false);
      setIsEditPolicyDialogOpen(false);
      setIsComparisonDialogOpen(false);
      setPolicyChanges([]);
      setOriginalPolicies([]);
    }
  }, [open]);

  const handleRowClick = useCallback((policy: PolicyWithChange) => {
    setSelectedPolicy(policy);
    setIsSheetOpen(true);
  }, []);

  const handleEditPolicy = useCallback(
    (updatedPolicy: Policy) => {
      // Find if this policy already has a change
      const existingChangeIndex = policyChanges.findIndex(
        (c) => c.policyId === updatedPolicy.id
      );

      const isCreating =
        existingChangeIndex !== -1 &&
        policyChanges[existingChangeIndex]?.action === 'CREATE';

      // Find original policy for comparison (used for account changes and setting removals)
      const originalPolicy = originalPolicies.find(
        (p) => p.id === updatedPolicy.id
      );

      // Calculate account changes for UPDATE actions
      let accountChanges: Extract<
        PolicyChangeProposal,
        { action: 'UPDATE' }
      >['policyAccountProposals'] = [];
      if (!isCreating && originalPolicy) {
        const originalAccountIds = new Set(
          originalPolicy.accounts.map((a) => a.id)
        );
        const newAccountIds = new Set(updatedPolicy.accounts.map((a) => a.id));

        // Find accounts to add
        updatedPolicy.accounts.forEach((account) => {
          if (!originalAccountIds.has(account.id)) {
            accountChanges.push({
              action: 'ADD',
              multichainAccountId: account.id,
            });
          }
        });

        // Find accounts to remove
        originalPolicy.accounts.forEach((account) => {
          if (!newAccountIds.has(account.id)) {
            accountChanges.push({
              action: 'REMOVE',
              multichainAccountId: account.id,
            });
          }
        });
      } else if (isCreating) {
        // For CREATE, all accounts are ADDs
        accountChanges = updatedPolicy.accounts.map((a) => ({
          action: 'ADD',
          multichainAccountId: a.id,
        }));
      }

      const limitationChange = updatedPolicy.limitation
        ? { ...updatedPolicy.limitation, shouldRemove: false as const }
        : !isCreating && originalPolicy?.limitation
          ? { shouldRemove: true as const }
          : null;

      // Build the change object
      const baseChange = {
        id: crypto.randomUUID(),
        policyId: updatedPolicy.id,
        policyName: updatedPolicy.name,
        policyDescription: updatedPolicy.description,
        policyType: updatedPolicy.type,
        policyTransactionType: updatedPolicy.transactionType,
        policyAccountProposals: accountChanges,
        limitation: limitationChange,
        ...(updatedPolicy.transactionType === 'TOKEN_TRANSFER' &&
        'tokenTransferCondition' in updatedPolicy
          ? { tokenTransferCondition: updatedPolicy.tokenTransferCondition }
          : {}),
        ...(updatedPolicy.transactionType === 'CONTRACT_INTERACTION' &&
        'contractInteractionCondition' in updatedPolicy
          ? {
              contractInteractionCondition:
                updatedPolicy.contractInteractionCondition,
            }
          : {}),
      };

      const change: PolicyChangeProposal = isCreating
        ? {
            ...baseChange,
            action: 'CREATE',
            networkId: updatedPolicy.networkId,
            // For CREATE: either set the setting or null (no shouldRemove: true)
            initiatorSetting: updatedPolicy.initiatorSetting
              ? {
                  shouldRemove: false,
                  ...updatedPolicy.initiatorSetting,
                }
              : null,
            approverSetting: updatedPolicy.approverSetting
              ? updatedPolicy.type === 'MANUAL_APPROVAL'
                ? {
                    shouldRemove: false,
                    ...updatedPolicy.approverSetting,
                  }
                : null
              : null,
          }
        : {
            ...baseChange,
            action: 'UPDATE',
            // Existing policy edits hard-code null because the network selector is
            // display-only; null means "leave the existing policy network unchanged."
            networkId: null,
            // For UPDATE: set, remove if original had it, or null (no change)
            initiatorSetting: updatedPolicy.initiatorSetting
              ? {
                  shouldRemove: false,
                  ...updatedPolicy.initiatorSetting,
                }
              : originalPolicy?.initiatorSetting
                ? { shouldRemove: true }
                : null,
            approverSetting: updatedPolicy.approverSetting
              ? updatedPolicy.type === 'MANUAL_APPROVAL'
                ? {
                    shouldRemove: false,
                    ...updatedPolicy.approverSetting,
                  }
                : originalPolicy?.approverSetting
                  ? { shouldRemove: true }
                  : null
              : originalPolicy?.approverSetting
                ? { shouldRemove: true }
                : null,
          };

      if (existingChangeIndex !== -1) {
        setPolicyChanges((prev) => [
          ...prev.slice(0, existingChangeIndex),
          change,
          ...prev.slice(existingChangeIndex + 1),
        ]);
      } else {
        setPolicyChanges((prev) => [...prev, change]);
      }

      setIsEditPolicyDialogOpen(false);
      setIsSheetOpen(false);
    },
    [policyChanges, originalPolicies]
  );

  const handleAddNewPolicy = useCallback((policy: Policy) => {
    // Create a CREATE change for the new policy
    const change: PolicyChangeProposal = {
      id: crypto.randomUUID(),
      action: 'CREATE',
      policyId: policy.id,
      policyName: policy.name,
      policyDescription: policy.description,
      policyType: policy.type,
      policyTransactionType: policy.transactionType,
      networkId: policy.networkId,
      policyAccountProposals: policy.accounts.map((a) => ({
        action: 'ADD',
        multichainAccountId: a.id,
      })),
      limitation: policy.limitation
        ? { ...policy.limitation, shouldRemove: false }
        : null,
      initiatorSetting: policy.initiatorSetting
        ? { shouldRemove: false, ...policy.initiatorSetting }
        : null,
      approverSetting:
        policy.type === 'MANUAL_APPROVAL' && policy.approverSetting
          ? { shouldRemove: false, ...policy.approverSetting }
          : null,
      ...(policy.transactionType === 'TOKEN_TRANSFER' &&
      'tokenTransferCondition' in policy
        ? { tokenTransferCondition: policy.tokenTransferCondition }
        : {}),
      ...(policy.transactionType === 'CONTRACT_INTERACTION' &&
      'contractInteractionCondition' in policy
        ? { contractInteractionCondition: policy.contractInteractionCondition }
        : {}),
    };

    setPolicyChanges((prev) => [...prev, change]);
  }, []);

  const hasChanges = useCallback(() => {
    return policyChanges.length > 0;
  }, [policyChanges]);

  const handleDialogClose = useCallback(
    (open: boolean) => {
      if (!open && hasChanges()) {
        // User is trying to close with unsaved changes
        setIsCloseConfirmOpen(true);
      } else {
        onOpenChange(open);
      }
    },
    [hasChanges, onOpenChange]
  );

  const handleCancel = useCallback(() => {
    if (hasChanges()) {
      setIsCloseConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  }, [hasChanges, onOpenChange]);

  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmOpen(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmOpen(false);
  }, []);

  // Prepare policy changes for API submission
  const getPolicyChangeProposalsForApi = useCallback(() => {
    return policyChanges.map((change) => {
      if (change.action === 'CREATE') {
        return {
          ...change,
          policyId: null,
        };
      }
      if (change.action === 'UPDATE') {
        return {
          ...change,
          networkId: null,
        };
      }
      return change;
    });
  }, [policyChanges]);

  // Actually submit the proposal (called after impact preview confirmation)
  // excludedPolicyChangeIds: IDs of policy changes to exclude from the proposal
  const submitProposal = useCallback(
    async (excludedPolicyChangeIds: string[] = []) => {
      if (!organization) {
        console.error('Organization not found');
        return;
      }

      setIsSubmitting(true);
      try {
        let policyChangeProposals = getPolicyChangeProposalsForApi();

        // Filter out excluded policy changes if any
        if (excludedPolicyChangeIds.length > 0 && impactPreview) {
          // Get the policyIds that should be excluded (from the impact preview)
          const excludedPolicyIds = new Set(
            impactPreview.policyChanges
              .filter((pc) => excludedPolicyChangeIds.includes(pc.id))
              .map((pc) => pc.policyId)
          );

          policyChangeProposals = policyChangeProposals.filter((change) => {
            // For UPDATE/DELETE, check against policyId
            if (change.policyId) {
              return !excludedPolicyIds.has(change.policyId);
            }
            return true;
          });
        }

        // If all changes were excluded, just close the dialog
        if (policyChangeProposals.length === 0) {
          onOpenChange(false);
          return;
        }

        const response = await createProposal({
          policyChangeProposals,
        });

        if (response.success && response.proposal) {
          onProposalCreated();
          onOpenChange(false);
        } else {
          const failedChanges = response.results.filter((r) => !r.success);
          const errorMessages = failedChanges
            .map((r) => {
              if (!r.success) {
                return `Change ${r.index + 1}: ${r.error}`;
              }
              return '';
            })
            .filter(Boolean)
            .join('\n');

          console.error('Failed to create policy proposal:', errorMessages);
          alert(`Failed to create policy proposal:\n\n${errorMessages}`);
        }
      } catch (err) {
        console.error('Failed to save policy changes:', err);
        alert('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
        setIsImpactPreviewOpen(false);
        setImpactPreview(null);
      }
    },
    [
      organization,
      getPolicyChangeProposalsForApi,
      createProposal,
      onProposalCreated,
      onOpenChange,
      impactPreview,
    ]
  );

  // Handle save button click - check for impact first
  const handleSaveChanges = useCallback(async () => {
    if (!organization) {
      console.error('Organization not found');
      return;
    }

    const policyChangeProposals = getPolicyChangeProposalsForApi();

    // Only submit if there are changes
    if (policyChangeProposals.length === 0) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      // Check for impact before submitting
      const impact = await previewImpact({
        organizationId: organization.id,
        policyChangeProposals,
      });

      if (impact.totalInvalidatedCount > 0) {
        // Show impact preview dialog for user confirmation
        setImpactPreview(impact);
        setIsImpactPreviewOpen(true);
        setIsSubmitting(false);
      } else {
        // No impact, proceed directly.
        // Empty array means no policy changes are excluded — submit all changes.
        await submitProposal([]);
      }
    } catch (err) {
      console.error('Failed to preview impact:', err);
      // If preview fails, proceed with submission anyway.
      // Empty array means no policy changes are excluded — submit all changes.
      await submitProposal([]);
    }
  }, [
    organization,
    getPolicyChangeProposalsForApi,
    previewImpact,
    submitProposal,
    onOpenChange,
  ]);

  const handleEditPolicyFromKebab = useCallback((policy: PolicyWithChange) => {
    setSelectedPolicy(policy);
    setIsSheetOpen(false);
    setIsEditPolicyDialogOpen(true);
  }, []);

  const handleRemovePolicy = useCallback(
    (policy: PolicyWithChange) => {
      // Check if this is a new policy (CREATE action)
      const existingChangeIndex = policyChanges.findIndex(
        (c) => c.policyId === policy.id && c.action === 'CREATE'
      );

      if (existingChangeIndex !== -1) {
        // If it's a new policy, just remove the CREATE change
        setPolicyChanges((prev) => [
          ...prev.slice(0, existingChangeIndex),
          ...prev.slice(existingChangeIndex + 1),
        ]);
      } else {
        // If it's an existing policy, add a DELETE change
        const deleteChange: PolicyChangeProposal = {
          id: crypto.randomUUID(),
          action: 'DELETE',
          policyId: policy.id,
        };
        setPolicyChanges((prev) => [...prev, deleteChange]);
      }
    },
    [policyChanges]
  );

  const handleUndoProposal = useCallback((policy: PolicyWithChange) => {
    // Remove all proposals/changes related to this policy
    setPolicyChanges((prev) => prev.filter((c) => c.policyId !== policy.id));
  }, []);

  const handleReviewChanges = useCallback((policy: PolicyWithChange) => {
    setSelectedPolicy(policy);
    setIsComparisonDialogOpen(true);
  }, []);

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={handleDialogClose}
        title="Edit policies"
        noPadding={true}
        bodyContainerClassName="bg-muted"
        footer={
          <div className="flex justify-center gap-2 max-w-xl w-full px-6">
            <Button size="lg" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="lg"
              onClick={handleSaveChanges}
              disabled={!hasChanges() || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        }
      >
        <div className="p-6">
          <div className="space-y-4">
            {/* New Policy Button */}
            <div className="flex justify-end">
              <Button onClick={() => setIsNewPolicyDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Policy
              </Button>
            </div>

            {/* Policies Table */}
            <PoliciesTable
              policies={displayedPolicies}
              onRowClick={handleRowClick}
              showEditStateBadges={true}
              emptyMessage="Add a new policy to get started."
              onEditPolicy={handleEditPolicyFromKebab}
              onReviewChanges={handleReviewChanges}
              onRemovePolicy={handleRemovePolicy}
              onUndoProposal={handleUndoProposal}
            />
          </div>
        </div>
      </FullscreenDialog>

      <PolicyDetailSheet
        policy={selectedPolicy}
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        isInEditMode={true}
        onEditClick={() => {
          setIsSheetOpen(false);
          setIsEditPolicyDialogOpen(true);
        }}
      />

      <NewPolicyDialog
        open={isNewPolicyDialogOpen}
        onOpenChange={setIsNewPolicyDialogOpen}
        onPolicyCreated={handleAddNewPolicy}
      />

      <EditPolicyDialog
        open={isEditPolicyDialogOpen}
        onOpenChange={setIsEditPolicyDialogOpen}
        policy={selectedPolicy}
        onPolicyUpdated={handleEditPolicy}
      />

      {selectedPolicy && getPolicyState(selectedPolicy) === 'modified' && (
        <PolicyChangeComparisonDialog
          open={isComparisonDialogOpen}
          onOpenChange={setIsComparisonDialogOpen}
          originalPolicy={
            originalPolicies.find((p) => p.id === selectedPolicy.id) ||
            selectedPolicy
          }
          modifiedPolicy={selectedPolicy}
          onSubmit={handleSaveChanges}
          onBack={() => setIsComparisonDialogOpen(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Unsaved changes confirmation dialog */}
      <Dialog open={isCloseConfirmOpen} onOpenChange={setIsCloseConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes to your policies. Are you sure you want
              to discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleCancelClose}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmClose}
              className="w-full sm:w-auto"
            >
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impact preview confirmation dialog */}
      <PolicyImpactPreviewDialog
        open={isImpactPreviewOpen}
        onOpenChange={setIsImpactPreviewOpen}
        impact={impactPreview}
        onConfirm={submitProposal}
        onCancel={() => {
          setIsImpactPreviewOpen(false);
          setImpactPreview(null);
        }}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
